import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, MessageSquare, AlignLeft, Calendar, ExternalLink } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Tooltip from '../ui/Tooltip';
import TaskAssigneeChip from '../TaskAssigneeChip';
import TaskLabelChips from '../TaskLabelChips';
import TaskPriorityBars from './TaskPriorityBars';
import TaskCardMenu from './TaskCardMenu';
import { TaskKeyChip, useTaskKey } from './TaskKeys';
import { TaskStatusButton } from './TaskStatus';
import { taskStatusOf } from '../../extensions/bundled/task-manager/taskStatus';
import { resolveLabels } from '../../extensions/bundled/task-manager/boardLabels';
import { formatDueChip, hasDescription as taskHasDescription } from '../../extensions/bundled/task-manager/taskCardMeta';
import { subtaskCounts } from '../../extensions/bundled/task-manager/subtasks';
import './TaskListRow.css';

/** One cell of the row grid. Every column is always there, empty or not, so
 *  the columns line up under the sticky header (card 6.2). */
function Cell({ col, children }) {
  return <div className={`task-board-list-cell task-board-list-cell--${col}`}>{children}</div>;
}

/** List view row (Boards Redesign card 6.2): a 44 px grid row whose columns
 *  match ListColumnHeader — grip · status · owner · priority · task ·
 *  labels · due · subtasks · comments · ⋯. Same interactions as the Kanban card: click
 *  the title to rename it, the status circle opens the status menu, the
 *  priority signal cycles, ⋯ opens Edit / Send to chat / Priority / Delete.
 *  The whole row is the drag handle (the 6 px activation distance keeps
 *  clicks working). */
function TaskListRow({ task, dragHandleProps, isOverlay, selected = false, onChange, onDelete, onOpenDetail, justCreatedRef, assignee = null, assignCtx = null, onAssign = null, labels = null, onShare = null }) {
  const taskKey = useTaskKey(task.id);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(task.title || '');
  const [menuOpen, setMenuOpen] = useState(false);
  // Right-click opens the ⋯ menu at the pointer, as on the cards (owner, 13.10.2026).
  const [contextAt, setContextAt] = useState(null);
  const titleRef = useRef(null);

  // Only rows created just now (via "Add task") start in edit mode, matching
  // TaskCard. Mounting an existing title-less row (e.g. on tab switch) must
  // not auto-focus it.
  useEffect(() => {
    if (!isOverlay && justCreatedRef?.current?.has(task.id)) {
      justCreatedRef.current.delete(task.id);
      setEditingTitle(true);
      setTitleDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  useEffect(() => {
    if (editingTitle && titleRef.current) {
      titleRef.current.focus();
      if (titleDraft) titleRef.current.select();
    }
  }, [editingTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitTitle = () => {
    setEditingTitle(false);
    const trimmed = titleDraft.trim();
    if (trimmed !== (task.title || '')) onChange({ title: trimmed || null });
  };

  const priority = task.priority || 'medium';
  const status = taskStatusOf(task);
  const due = formatDueChip(task.dueDate);
  const commentCount = (task.comments || []).length;
  const subs = subtaskCounts(task);
  const subsPct = subs.total ? Math.round((subs.done / subs.total) * 100) : 0;
  const subsLabel = `${subs.done} of ${subs.total} subtask${subs.total === 1 ? '' : 's'} done`;
  const described = taskHasDescription(task);
  const classes = [
    'task-board-list-row',
    task.done && 'task-board-list-row--done',
    status === 'dismissed' && 'task-board-list-row--dismissed',
    selected && 'task-board-list-row--selected',
    menuOpen && 'task-board-list-row--active',
    isOverlay && 'task-board-list-row--overlay',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      data-task-id={task.id}
      {...(dragHandleProps || {})}
      onContextMenu={(e) => {
        if (isOverlay || editingTitle) return;
        e.preventDefault();
        e.stopPropagation();
        setContextAt((prev) => ({ x: e.clientX, y: e.clientY, n: (prev?.n || 0) + 1 }));
      }}
    >
      <Cell col="grip">
        <GripVertical size={13} className="task-board-list-row-grip" aria-hidden />
      </Cell>
      {/* Status first, then owner, then priority (design of record, card 6.2;
          supersedes the 01.10 "priority fully left" order like the card did
          in list 2). Fixed-width facts sit in the left gutter so they line up. */}
      <Cell col="status">
        <TaskStatusButton className="task-board-list-row-check" task={task} onChange={onChange} />
      </Cell>
      <Cell col="owner">
        <TaskAssigneeChip
          assigneeId={task.assigneeId}
          member={assignee}
          members={assignCtx?.members}
          currentUserId={assignCtx?.currentUserId}
          onAssign={onAssign}
          size={28}
        />
      </Cell>
      <Cell col="priority">
        {/* Done rows keep their priority too (owner, 13.10.2026). */}
        <TaskPriorityBars priority={priority} onCycle={(next) => onChange({ priority: next })} />
      </Cell>
      <Cell col="task">
        <TaskKeyChip taskKey={taskKey} className="task-board-list-row-key" />
        {editingTitle ? (
          <input
            ref={titleRef}
            type="text"
            className="task-board-list-row-title-input"
            aria-label="Task title"
            value={titleDraft}
            placeholder="Enter task title..."
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitle();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setTitleDraft(task.title || '');
                setEditingTitle(false);
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`task-board-list-row-title${task.title ? '' : ' task-board-list-row-title--empty'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isOverlay) return;
              setTitleDraft(task.title || '');
              setEditingTitle(true);
            }}
          >
            {task.title || 'Click to add title...'}
          </span>
        )}
        {described && (
          <Tooltip content="Has a description" side="top">
            <span className="task-board-list-row-desc" role="img" aria-label="Has a description" data-has-description="true">
              <AlignLeft size={12} aria-hidden />
            </span>
          </Tooltip>
        )}
        {/* Quick open, as on the Kanban card (owner, 19.10.2026). Its
            pointer-down is stopped so the row does not arm a drag. */}
        {!isOverlay && onOpenDetail && (
          <Tooltip content="Open task" side="top">
            <button
              type="button"
              className="task-card-open-btn task-board-list-row-open"
              aria-label="Open task"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onOpenDetail();
              }}
            >
              <ExternalLink size={11} aria-hidden />
            </button>
          </Tooltip>
        )}
      </Cell>
      <Cell col="labels">
        <TaskLabelChips labels={labels} max={2} className="task-board-list-row-labels" />
      </Cell>
      <Cell col="due">
        {due && (
          <Tooltip content={due.overdue ? `Overdue — due ${due.full}` : `Due ${due.full}`} side="top">
            <span
              className={`task-board-list-row-due${due.overdue ? ' task-board-list-row-due--overdue' : ''}`}
              aria-label={due.overdue ? `Overdue: due ${due.full}, ${due.daysLate} days late` : `Due ${due.full}`}
              data-overdue={due.overdue ? 'true' : 'false'}
            >
              {/* A calendar for both, like the artboard: red says "late". */}
              <Calendar size={11} strokeWidth={2.2} aria-hidden />
              {/* The short day only: "· N d late" does not fit the 96 px
                  column; the red chip and the tooltip carry the lateness. */}
              {due.text.split(' · ')[0]}
            </span>
          </Tooltip>
        )}
      </Cell>
      <Cell col="subtasks">
        {subs.total > 0 && (
          <Tooltip content={subsLabel} side="top">
            <span className="task-board-list-row-subtasks" aria-label={subsLabel} data-subtasks={`${subs.done}/${subs.total}`}>
              {/* The artboard's 28 px mini bar, then the count. */}
              <span className="task-board-list-row-subtasks-bar" aria-hidden>
                <span style={{ width: `${subsPct}%` }} />
              </span>
              {subs.done}/{subs.total}
            </span>
          </Tooltip>
        )}
      </Cell>
      <Cell col="comments">
        {commentCount > 0 && (
          <Tooltip content={`${commentCount} comment${commentCount === 1 ? '' : 's'}`} side="top">
            <span className="task-board-list-row-meta" aria-label={`${commentCount} comment${commentCount === 1 ? '' : 's'}`}>
              <MessageSquare size={11} aria-hidden />
              {commentCount}
            </span>
          </Tooltip>
        )}
      </Cell>
      <Cell col="menu">
        {!isOverlay && (
          <TaskCardMenu
            taskKey={taskKey}
            title={task.title || ''}
            priority={priority}
            onOpenDetail={onOpenDetail}
            onShare={onShare}
            onDelete={onDelete}
            onPriority={(p) => onChange({ priority: p })}
            onOpenChange={setMenuOpen}
            contextAt={contextAt}
          />
        )}
      </Cell>
    </div>
  );
}

// Stable props for the memoised row, like the Kanban card (owner, 13.10.2026:
// big boards): a drag step re-renders only rows whose task changed.
export const SortableTaskRow = React.memo(function SortableTaskRow({ task, selected = false, dragDisabled = false, onOpenTask, onChangeTask, onDeleteTask, justCreatedRef, memberById, assignCtx, palette }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `task:${task.id}`,
    disabled: dragDisabled,
  });
  const id = task.id;
  const labels = useMemo(() => resolveLabels(task, palette), [task, palette]);
  const dragHandleProps = useMemo(() => ({ ...attributes, ...listeners }), [attributes, listeners]);
  const onAssign = useMemo(() => (assignCtx ? (to) => onChangeTask(id, { assigneeId: to }) : null), [assignCtx, onChangeTask, id]);
  const onShare = useMemo(() => (assignCtx ? () => assignCtx.onShare?.(task) : null), [assignCtx, task]);
  const onChange = useCallback((patch) => onChangeTask(id, patch), [onChangeTask, id]);
  const onDelete = useCallback(() => onDeleteTask(id), [onDeleteTask, id]);
  const onOpenDetail = useCallback(() => onOpenTask(id), [onOpenTask, id]);
  return (
    // While dragged, the row's place shows as a dashed slot, like the card's.
    <div ref={setNodeRef} className={isDragging ? 'task-board-list-row-sortable task-board-list-row-slot' : 'task-board-list-row-sortable'} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <TaskListRow
        task={task}
        selected={selected}
        labels={labels}
        assignee={task.assigneeId ? memberById?.get(String(task.assigneeId)) || null : null}
        assignCtx={assignCtx}
        onAssign={onAssign}
        onShare={onShare}
        dragHandleProps={dragHandleProps}
        onChange={onChange}
        onDelete={onDelete}
        onOpenDetail={onOpenDetail}
        justCreatedRef={justCreatedRef}
      />
    </div>
  );
});

// Memoised (owner, 13.10.2026: big boards) — SortableTaskRow hands it stable props.
export default React.memo(TaskListRow);
